// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { SystemDocumentTypes, User } from '@interfaces/user.interfaces';
import { NavService } from '@services/nav.service';
import { UserService } from '@services/user.service';
import { debounceTime, firstValueFrom, takeWhile } from 'rxjs';
import { SystemDocumentModalComponent } from 'src/app/modals/system-document-modal/system-document-modal.component';
import { SuccessModalComponent } from 'src/app/modals/success-modal/success-modal.component';
import { showFormErrors } from 'src/app/utils/form-utils';
import { SystemDocument } from '@interfaces/document.interfaces';
import { environment } from 'src/environments/environment';
import { fullRoute as AdminGettingStartedRoute } from '../../shared/admin-getting-started/admin-getting-started.component';
import { SharedService } from '@services/shared.service';
import { ModalSize } from '@interfaces/shared.interfaces';
import { DealershipService } from '../../services/dealership.service';
import { SettingsService } from '@services/settings.service';

@Component({
    selector: 'dealer-help',
    templateUrl: './dealer-help.component.html',
    styleUrls: ['./dealer-help.component.scss'],
})
export class DealerHelpComponent implements OnInit, OnDestroy {
    @ViewChild(FormGroupDirective) formGroupDirective: FormGroupDirective;
    public user: User = null;
    public dealerName = '';
    public oemDealerId = '';
    helpForm: FormGroup;
    enabledDetails: FormGroup;
    faqList: any[] = [];
    emailSubjects: any[] = [];
    panels: any;
    private isComponentAlive = true;
    public isLoading = false;
    public selectedSubject: any;
    public releaseNotes: SystemDocument[] = [];
    public requiredDocs: SystemDocument[] = [];
    public isProd: boolean = environment.production;
    public supportEmail: string;
    public supportPhone: string;
    public oemCode: string;
    public isPddj: boolean;
    public prefixedSubjects: any[] = [];
    public isTimeSensitiveCheck = false;
    public generalTrainingLink = 'https://forms.office.com/r/yNPJ4uQYth';
    public systemAdminTrainingLink = 'https://forms.office.com/r/Zq1Fb5Rt1M';

    constructor(
        private readonly route: ActivatedRoute,
        private fb: FormBuilder,
        private dialog: MatDialog,
        private readonly navService: NavService,
        private readonly userService: UserService,
        private readonly router: Router,
        private readonly sharedService: SharedService,
        private readonly dealershipService: DealershipService,
        private readonly settingsService: SettingsService
    ) {}

    async ngOnInit(): Promise<void> {
        this.isPddj = window.location.href.includes('/pddj');
        this.user = this.route.snapshot.data?.user;
        this.faqList = this.route.snapshot.data?.help[0];
        this.emailSubjects = this.route.snapshot.data?.help[1];
        this.buildForm();
        this.navService.setTitle('Help');

        for (let i = 0; i <= this.emailSubjects.length - 1; i++) {
            this.prefixedSubjects.push(`${this.emailSubjects[i].reason}`);
        }

        this.helpForm.get('isTimeSensitive').valueChanges.subscribe((checked) => {
            this.isTimeSensitiveCheck = checked;
        });

        ({ oemCode: this.oemCode, oemDealerId: this.oemDealerId, name: this.dealerName } = await firstValueFrom(this.dealershipService.dealer$));
        ({ supportEmail: this.supportEmail, supportPhone: this.supportPhone } = await this.settingsService.getOemSettings(this.oemCode));

        this.helpForm.controls.email.valueChanges
            .pipe(
                takeWhile(() => this.isComponentAlive),
                debounceTime(500)
            )
            .subscribe((value) => {
                value.match(/\s+$/) && this.helpForm.controls.email.setValue(value.trim());
            });
    }

    ngOnDestroy(): void {
        this.isComponentAlive = false;
    }

    buildForm() {
        this.helpForm = this.fb.group({
            isTimeSensitive: [false],
            firstName: [
                { value: this.user.firstName, disabled: true },
                Validators.compose([Validators.required, Validators.maxLength(128), Validators.pattern('')]),
            ],
            lastName: [
                { value: this.user.lastName, disabled: true },
                Validators.compose([Validators.required, Validators.maxLength(128), Validators.pattern('')]),
            ],
            email: [{ value: this.user.email, disabled: true }, Validators.compose([Validators.required, Validators.maxLength(128), Validators.pattern('')])],
            //NOTE: the Callback # formatting should not be restricted to the (xxx) xxx-xxxx because the dealers often have Ext #'s they need to add. Leave this field unformatted
            callback: ['', Validators.compose([Validators.maxLength(50), Validators.pattern('')])],
            subject: ['', Validators.compose([Validators.required, Validators.maxLength(128), Validators.pattern('')])],
            message: ['', Validators.compose([Validators.required, Validators.maxLength(300), Validators.minLength(5), Validators.pattern('')])],
        });
    }

    private resetForm() {
        this.helpForm.reset();
        this.formGroupDirective.resetForm();
        this.buildForm();
    }

    private async checkDocs(docType: string): Promise<SystemDocument[]> {
        // Call to load documents only when trying to view documents and only once
        if (docType.includes('Release')) {
            return this.releaseNotes.length
                ? this.releaseNotes
                : (this.releaseNotes = await firstValueFrom(this.userService.getReleaseNotes(SystemDocumentTypes.releaseNotes)));
        }

        return this.requiredDocs.length ? this.requiredDocs : (this.requiredDocs = await firstValueFrom(this.userService.getHelpPageSystemDocuments()));
    }

    public sendMessage(): void {
        if (this.helpForm.status === 'INVALID') {
            showFormErrors(this.helpForm.controls);
            return;
        }

        this.isLoading = true;
        let selectedSubjectTitle = this.helpForm.get('subject').value ? this.helpForm.get('subject').value : 'Unknown Issue';
        selectedSubjectTitle += ` for ${this.dealerName} (${this.oemDealerId})`;

        const payload = {
            userId: this.user.userId,
            dealId: null,
            firstName: this.user.firstName,
            lastName: this.user.lastName,
            message: this.helpForm.controls.message.value,
            subject: this.isTimeSensitiveCheck ? 'TIME SENSITIVE: ' + selectedSubjectTitle : selectedSubjectTitle,
            phoneFrom: this.helpForm.controls.callback.value ? this.helpForm.controls.callback.value : this.user.phone,
            emailFrom: this.user.email,
            emailTo: this.supportEmail,
            oemCode: this.oemCode,
            mobilePhone: this.user.phone ? this.user.phone : 'no mobile phone available',
            isCustomer: false,
        };

        if (!this.validateRequest(payload)) {
            this.dialog.open(SuccessModalComponent, {
                width: '700px',
                data: {
                    title: 'Unable To Send Message',
                    content: 'Please reload page. If you need immediate assistance, please call technical support',
                },
            });

            return;
        }

        this.userService
            .sendHelpMessage(payload)
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((messageSent) => {
                this.isLoading = false;
                if (messageSent) {
                    this.dialog.open(SuccessModalComponent, {
                        width: '700px',
                        data: {
                            title: 'Message Sent',
                            content: `Your message was sent successfully. Someone will get back to you shortly. If you need immediate assistance, please call ${this.supportPhone ?? '(808) 505-3335'}.`, //the one place we should hard code the support number, on failure.
                        },
                    });
                    this.resetForm();
                }
            });
    }

    private validateRequest(payload: any) {
        if (
            payload.userId &&
            payload.firstName &&
            payload.lastName &&
            payload.message &&
            payload.subject &&
            payload.emailFrom &&
            payload.emailTo &&
            payload.oemCode &&
            payload.mobilePhone
        ) {
            return true;
        }

        return false;
    }

    public async systemDocumentDialog(docType: string) {
        this.isLoading = true;
        const docs = await this.checkDocs(docType);
        this.isLoading = false;
        this.dialog.open(SystemDocumentModalComponent, {
            width: this.sharedService.getModalWidth(ModalSize.Medium) + 'px',
            height: this.sharedService.getModalHeight(ModalSize.Medium) + 'px',

            closeOnNavigation: false,
            data: {
                modalSize: ModalSize.Medium,
                doc: docs[0].systemDocumentTypeId === 3 ? docs : docs.filter((doc) => doc.systemDocumentDescription === docType)[0],
            },
        });
    }

    routeToFTC() {
        const url = this.router.serializeUrl(this.router.createUrlTree(['agreements', 'FTC']));
        window.open(url, '_blank');
    }

    routeToGetStarted() {
        const url = this.router.serializeUrl(this.router.createUrlTree([AdminGettingStartedRoute]));
        window.open(url, '_blank');
    }

    routeToTraining(type: 'general-user' | 'system-admin') {
        switch (type) {
            case 'system-admin':
                window.open(this.systemAdminTrainingLink, '_blank');
                break;
            case 'general-user':
            default:
                window.open(this.generalTrainingLink, '_blank');
                break;
        }
    }
}
